import { THREE } from "../config/ThreeConfig.js";
import { uiSpriteUrl } from "../assets/UIIcons.js";
import { isTestEnvironment } from "./runtimeEnv.js";

/**
 * Gameplay Visual Feedback System
 * Adds screen effects, 3D particles, and visual polish for gameplay actions
 */

export function initGameplayFeedback(uiManager, sceneManager) {
  // Screen shake state
  let shakeIntensity = 0;
  let shakeDuration = 0;
  let originalCameraPos = null;
  let lastShakeAt = 0;
  let lastHitEffectAt = 0;

  // Active effects tracking
  const activeEffects = {
    particles: [],
    damageNumbers: [],
    screenOverlays: []
  };

  // Shared soft particle mask (avoids "square pixel" particles).
  let softParticleTexture = null;
  const getSoftParticleTexture = () => {
    if (softParticleTexture) return softParticleTexture;
    const size = 32;
    const data = new Uint8Array(size * size * 4);
    for (let y = 0; y < size; y++) {
      for (let x = 0; x < size; x++) {
        const dx = (x + 0.5) / size * 2 - 1;
        const dy = (y + 0.5) / size * 2 - 1;
        const r = Math.sqrt(dx * dx + dy * dy);
        const a = Math.max(0, 1 - r);
        const i = (y * size + x) * 4;
        data[i] = 255;
        data[i + 1] = 255;
        data[i + 2] = 255;
        data[i + 3] = Math.round(Math.pow(a, 2.2) * 255);
      }
    }
    const tex = new THREE.DataTexture(data, size, size, THREE.RGBAFormat);
    tex.needsUpdate = true;
    softParticleTexture = tex;
    return tex;
  };

  // Sprite textures (best effort; skip entirely in tests).
  const canUseSprites = !isTestEnvironment() && typeof document !== "undefined" && typeof Image !== "undefined";
  const textureLoader = canUseSprites ? new THREE.TextureLoader() : null;
  const texturePromiseCache = new Map(); // url -> Promise<THREE.Texture|null>
  const getTexture = (url) => {
    const key = String(url || "");
    if (!key || !textureLoader) return Promise.resolve(null);
    if (texturePromiseCache.has(key)) return texturePromiseCache.get(key);
    const p = new Promise((resolve) => {
      try {
        textureLoader.load(
          key,
          (tex) => resolve(tex || null),
          undefined,
          () => resolve(null)
        );
      } catch {
        resolve(null);
      }
    });
    texturePromiseCache.set(key, p);
    return p;
  };

  // Particle system for explosions
  function createParticleExplosion(position, color = 0xff6600, count = 50, size = 2) {
    if (!sceneManager.scene) return null;

    const geometry = new THREE.BufferGeometry();
    const positions = new Float32Array(count * 3);
    const velocities = [];
    const colors = new Float32Array(count * 3);

    const baseColor = new THREE.Color(color);

    for (let i = 0; i < count; i++) {
      const i3 = i * 3;
      positions[i3] = position.x;
      positions[i3 + 1] = position.y;
      positions[i3 + 2] = position.z;

      // Explosive velocity
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.random() * Math.PI;
      const speed = 20 + Math.random() * 80;

      velocities.push({
        x: speed * Math.sin(phi) * Math.cos(theta),
        y: speed * Math.sin(phi) * Math.sin(theta),
        z: speed * Math.cos(phi)
      });

      // Color variation
      const variation = (Math.random() - 0.5) * 0.2;
      colors[i3] = Math.min(1, baseColor.r + variation);
      colors[i3 + 1] = Math.min(1, baseColor.g + variation);
      colors[i3 + 2] = Math.min(1, baseColor.b + variation);
    }

    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));

    const material = new THREE.PointsMaterial({
      size: size,
      map: getSoftParticleTexture(),
      alphaTest: 0.08,
      vertexColors: true,
      transparent: true,
      opacity: 1,
      depthWrite: false,
      blending: THREE.AdditiveBlending
    });

    const particles = new THREE.Points(geometry, material);
    sceneManager.scene.add(particles);

    return {
      object3D: particles,
      velocities,
      age: 0,
      maxAge: 1.5,
      update: function(deltaTime) {
        this.age += deltaTime;
        const positions = this.object3D.geometry.attributes.position.array;

        for (let i = 0; i < this.velocities.length; i++) {
          const i3 = i * 3;
          positions[i3] += this.velocities[i].x * deltaTime;
          positions[i3 + 1] += this.velocities[i].y * deltaTime;
          positions[i3 + 2] += this.velocities[i].z * deltaTime;

          // Gravity effect
          this.velocities[i].y -= 20 * deltaTime;
          // Drag
          this.velocities[i].x *= 0.98;
          this.velocities[i].y *= 0.98;
          this.velocities[i].z *= 0.98;
        }

        this.object3D.geometry.attributes.position.needsUpdate = true;
        this.object3D.material.opacity = 1 - (this.age / this.maxAge);

        // Keep active until `isExpired()` returns true.
        return true;
      },
      isExpired: function() {
        return this.age >= this.maxAge;
      },
      dispose: function() {
        sceneManager.scene.remove(this.object3D);
        this.object3D.geometry.dispose();
        this.object3D.material.dispose();
      }
    };
  }

  // Chunky debris burst (spins dramatically; fades quickly for readability).
  function createDebrisBurst(position, color = 0xffffff, count = 8, size = 6, ttl = 1.2) {
    if (!sceneManager.scene) return null;

    const pieces = [];
    const baseColor = new THREE.Color(color);
    for (let i = 0; i < count; i++) {
      const s = size * (0.55 + Math.random() * 0.75);
      const geom = new THREE.BoxGeometry(s, s * (0.55 + Math.random() * 0.8), s * (0.55 + Math.random() * 0.8));
      const mat = new THREE.MeshBasicMaterial({
        color: baseColor,
        transparent: true,
        opacity: 0.95,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
      });
      const mesh = new THREE.Mesh(geom, mat);
      mesh.position.copy(position);
      mesh.position.x += (Math.random() - 0.5) * 6;
      mesh.position.y += (Math.random() - 0.5) * 6;
      mesh.position.z += (Math.random() - 0.5) * 6;

      const theta = Math.random() * Math.PI * 2;
      const phi = Math.random() * Math.PI;
      const spd = 45 + Math.random() * 120;
      const vel = new THREE.Vector3(
        spd * Math.sin(phi) * Math.cos(theta),
        spd * Math.sin(phi) * Math.sin(theta),
        spd * Math.cos(phi)
      );
      const ang = new THREE.Vector3(
        (Math.random() - 0.5) * 7,
        (Math.random() - 0.5) * 7,
        (Math.random() - 0.5) * 7
      );

      sceneManager.scene.add(mesh);
      pieces.push({ mesh, vel, ang });
    }

    return {
      object3D: null,
      age: 0,
      maxAge: ttl,
      update: function (deltaTime) {
        this.age += deltaTime;
        const t = Math.max(0, Math.min(1, this.age / this.maxAge));
        for (const p of pieces) {
          p.mesh.position.add(p.vel.clone().multiplyScalar(deltaTime));
          p.mesh.rotation.x += p.ang.x * deltaTime;
          p.mesh.rotation.y += p.ang.y * deltaTime;
          p.mesh.rotation.z += p.ang.z * deltaTime;

          // Drag + drop for dramatic spins.
          p.vel.multiplyScalar(0.985);
          p.vel.y -= 70 * deltaTime;

          if (p.mesh.material) p.mesh.material.opacity = Math.max(0, 0.95 * (1 - t));
        }
        return true;
      },
      isExpired: function () {
        return this.age >= this.maxAge;
      },
      dispose: function () {
        for (const p of pieces) {
          try {
            sceneManager.scene.remove(p.mesh);
            p.mesh.geometry?.dispose?.();
            p.mesh.material?.dispose?.();
          } catch {
            // ignore
          }
        }
      },
    };
  }

  // Damage number popup
  function showDamageNumber(damage, position, isCritical = false) {
    const numberEl = document.createElement('div');
    numberEl.textContent = damage;
    numberEl.style.cssText = `
      position: fixed;
      color: ${isCritical ? '#ff00ff' : 'rgba(0, 255, 255, 0.92)'};
      font-size: ${isCritical ? '24px' : '18px'};
      font-weight: bold;
      font-family: 'JetBrains Mono', monospace;
      text-shadow: 0 0 10px ${isCritical ? '#ff00ff' : 'rgba(0, 255, 255, 0.55)'};
      pointer-events: none;
      z-index: 2000;
      transition: all 0.5s ease-out;
    `;

    // Project 3D position to screen
    if (sceneManager.camera) {
      const vector = position.clone();
      vector.project(sceneManager.camera);
      const x = (vector.x * 0.5 + 0.5) * window.innerWidth;
      const y = (-(vector.y * 0.5) + 0.5) * window.innerHeight;
      numberEl.style.left = `${x}px`;
      numberEl.style.top = `${y}px`;
    }

    document.body.appendChild(numberEl);

    // Animate
    requestAnimationFrame(() => {
      numberEl.style.transform = 'translateY(-50px) scale(1.2)';
      numberEl.style.opacity = '0';
    });

    setTimeout(() => numberEl.remove(), 500);
  }

  function spawnShieldImpactSprite(position, { size = 26, ttl = 0.35, opacity = 0.9 } = {}) {
    if (!canUseSprites || !sceneManager.scene) return;
    const url = uiSpriteUrl("shield_impact", 256);
    getTexture(url).then((base) => {
      if (!base || !sceneManager.scene) return;
      const tex = base.clone();
      tex.needsUpdate = true;

      const mat = new THREE.SpriteMaterial({
        map: tex,
        color: 0xffffff,
        transparent: true,
        opacity,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
      });
      const sprite = new THREE.Sprite(mat);
      sprite.position.copy(position);
      sprite.scale.set(size, size, 1);
      sceneManager.scene.add(sprite);

      const effect = {
        object3D: sprite,
        age: 0,
        maxAge: ttl,
        update: function (deltaTime) {
          this.age += deltaTime;
          const t = Math.max(0, Math.min(1, this.age / this.maxAge));
          sprite.material.opacity = opacity * (1 - t);
          const s = size * (1 + t * 0.6);
          sprite.scale.set(s, s, 1);
          return true;
        },
        isExpired: function () {
          return this.age >= this.maxAge;
        },
        dispose: function () {
          sceneManager.scene.remove(sprite);
          sprite.material.map?.dispose?.();
          sprite.material.dispose?.();
        },
      };
      activeEffects.particles.push(effect);
    });
  }

  function spawnExplosionSheetSprite(position, { size = 42, fps = 22 } = {}) {
    if (!canUseSprites || !sceneManager.scene) return;
    const url = uiSpriteUrl("explosion_sheet", 256);
    getTexture(url).then((base) => {
      if (!base || !sceneManager.scene) return;

      const cols = 4;
      const rows = 2;
      const frames = cols * rows;

      const tex = base.clone();
      tex.needsUpdate = true;
      tex.wrapS = THREE.RepeatWrapping;
      tex.wrapT = THREE.RepeatWrapping;
      tex.repeat.set(1 / cols, 1 / rows);
      tex.offset.set(0, 1 - 1 / rows);

      const mat = new THREE.SpriteMaterial({
        map: tex,
        color: 0xffffff,
        transparent: true,
        opacity: 1,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
      });
      const sprite = new THREE.Sprite(mat);
      sprite.position.copy(position);
      sprite.scale.set(size, size, 1);
      sceneManager.scene.add(sprite);

      const effect = {
        object3D: sprite,
        age: 0,
        maxAge: frames / fps,
        update: function (deltaTime) {
          this.age += deltaTime;
          const f = Math.floor(this.age * fps);
          if (f >= frames) return false;
          const col = f % cols;
          const row = Math.floor(f / cols);
          tex.offset.x = col / cols;
          tex.offset.y = 1 - (row + 1) / rows;
          sprite.material.opacity = Math.max(0, 1 - (this.age / this.maxAge));
          return true;
        },
        isExpired: function () {
          return this.age >= this.maxAge;
        },
        dispose: function () {
          sceneManager.scene.remove(sprite);
          sprite.material.map?.dispose?.();
          sprite.material.dispose?.();
        },
      };
      activeEffects.particles.push(effect);
    });
  }

  // Screen flash effect
  function createScreenFlash(color, duration = 0.3, intensity = 0.3) {
    const flash = document.createElement('div');
    flash.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: ${color};
      opacity: ${intensity};
      pointer-events: none;
      z-index: 1500;
      transition: opacity ${duration}s ease;
    `;
    document.body.appendChild(flash);

    setTimeout(() => {
      flash.style.opacity = '0';
      setTimeout(() => flash.remove(), duration * 1000);
    }, 50);
  }

  // Weapon trail effect
  function createWeaponTrailEffect(startPos, endPos, color = 0x00ffff) {
    if (!sceneManager.scene) return;

    const direction = new THREE.Vector3().subVectors(endPos, startPos);
    const length = direction.length();
    const midPoint = new THREE.Vector3().addVectors(startPos, endPos).multiplyScalar(0.5);

    // Keep streaks readable without overwhelming the screen.
    // Crossed quads are thinner/cleaner than a fat tube and stay visible.
    const makeQuad = (width) => {
      const g = new THREE.PlaneGeometry(Math.max(0.01, width), Math.max(0.01, length), 1, 1);
      g.rotateX(-Math.PI / 2); // long axis -> Z
      return g;
    };

    const material = new THREE.MeshBasicMaterial({
      color: color,
      transparent: true,
      opacity: 0.55,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      depthTest: false,
      fog: false,
      side: THREE.DoubleSide,
      toneMapped: false,
    });

    const trail = new THREE.Group();
    trail.renderOrder = 22;
    trail.position.copy(midPoint);
    trail.lookAt(endPos);

    const q1 = new THREE.Mesh(makeQuad(0.85), material);
    const q2 = new THREE.Mesh(makeQuad(0.85), material.clone());
    q2.rotation.z = Math.PI / 2;
    q1.frustumCulled = false;
    q2.frustumCulled = false;
    trail.add(q1);
    trail.add(q2);
    sceneManager.scene.add(trail);

    // Fade out
    let age = 0;
    const maxAge = 0.11;

    function updateTrail(deltaTime) {
      age += deltaTime;
      const t = Math.max(0, 1 - age / maxAge);
      q1.material.opacity = 0.55 * t;
      q2.material.opacity = 0.55 * t;
      trail.scale.setScalar(0.78 + t * 0.28);

      if (age >= maxAge) {
        sceneManager.scene.remove(trail);
        q1.geometry.dispose();
        q2.geometry.dispose();
        q1.material.dispose();
        q2.material.dispose();
        return false;
      }
      return true;
    }

    activeEffects.particles.push({ update: updateTrail });
  }

  return {
    // Screen shake with camera restoration
    shakeScreen: (intensity, duration) => {
      const now =
        (globalThis.performance && typeof globalThis.performance.now === "function")
          ? globalThis.performance.now()
          : Date.now();
      if (now - lastShakeAt < 180) return;
      lastShakeAt = now;

      shakeIntensity = intensity;
      shakeDuration = duration;

      // Store original position
      if (sceneManager.camera && !originalCameraPos) {
        originalCameraPos = sceneManager.camera.position.clone();
      }

      // Visual feedback
      if (uiManager.createMissionMsg) {
        uiManager.createMissionMsg("IMPACT DETECTED", 'error');
      }
    },

    // Player hit effect
    playHitEffect: () => {
      const now =
        (globalThis.performance && typeof globalThis.performance.now === "function")
          ? globalThis.performance.now()
          : Date.now();
      if (now - lastHitEffectAt < 220) return;
      lastHitEffectAt = now;

      // Screen flash
      createScreenFlash('rgba(255, 0, 0, 0.2)', 0.3, 0.3);

      // Vignette effect
      const vignette = document.createElement('div');
      vignette.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: radial-gradient(circle, transparent 50%, rgba(255, 0, 0, 0.4) 100%);
        pointer-events: none;
        z-index: 1499;
        opacity: 1;
        transition: opacity 0.5s ease;
      `;
      document.body.appendChild(vignette);

      setTimeout(() => {
        vignette.style.opacity = '0';
        setTimeout(() => vignette.remove(), 500);
      }, 200);

      // UI feedback
      if (uiManager.createMissionMsg) {
        uiManager.createMissionMsg("SHIELD IMPACT", 'warning');
      }
    },

    // Boost effect
    playBoostEffect: () => {
      // Screen warp effect
      createScreenFlash('rgba(0, 255, 255, 0.1)', 0.2, 0.1);

      // Speed lines
      const speedLines = document.createElement('div');
      speedLines.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: repeating-linear-gradient(
          90deg,
          transparent,
          transparent 50px,
          rgba(0, 255, 255, 0.1) 50px,
          rgba(0, 255, 255, 0.1) 60px
        );
        pointer-events: none;
        z-index: 1000;
        opacity: 0.8;
        transition: opacity 0.3s ease;
      `;
      document.body.appendChild(speedLines);

      setTimeout(() => {
        speedLines.style.opacity = '0';
        setTimeout(() => speedLines.remove(), 300);
      }, 500);

      // UI feedback
      if (uiManager.createMissionMsg) {
        uiManager.createMissionMsg("BOOST ENGAGED", 'info');
      }
    },

    // Bomb explosion effect
    playBombEffect: () => {
      // Central explosion flash
      createScreenFlash('rgba(255, 102, 0, 0.3)', 0.5, 0.3);

      // Shockwave ring
      const shockwave = document.createElement('div');
      shockwave.style.cssText = `
        position: fixed;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 50px;
        height: 50px;
        border: 3px solid #ff6600;
        border-radius: 50%;
        box-shadow: 0 0 20px #ff6600;
        z-index: 1500;
        pointer-events: none;
        transition: all 0.5s ease-out;
      `;
      document.body.appendChild(shockwave);

      // Animate shockwave
      requestAnimationFrame(() => {
        shockwave.style.width = '500px';
        shockwave.style.height = '500px';
        shockwave.style.opacity = '0';
      });

      setTimeout(() => shockwave.remove(), 500);

      // Screen shake
      shakeIntensity = 0.5;
      shakeDuration = 0.5;

      // UI feedback
      if (uiManager.createMissionMsg) {
        uiManager.createMissionMsg("ORDNANCE DEPLOYED", 'success');
      }
    },

    // Special ability activation (bold + readable).
    playSpecialEffect: ({ character, name } = {}) => {
      const id = String(character || "").toLowerCase();
      const colors = {
        honeybadger: 'rgba(255, 102, 0, 0.16)',
        octopus: 'rgba(0, 255, 255, 0.12)',
        elephant: 'rgba(0, 255, 255, 0.10)',
        mantisshrimp: 'rgba(255, 0, 255, 0.12)',
        ant: 'rgba(0, 255, 102, 0.12)',
      };
      createScreenFlash(colors[id] || 'rgba(0, 255, 255, 0.12)', 0.22, 0.18);

      // Quick expanding ring (HUD layer) so it's obvious even if the 3D effect is subtle.
      const ring = document.createElement('div');
      ring.style.cssText = `
        position: fixed;
        left: 50%;
        top: 52%;
        transform: translate(-50%, -50%);
        width: 40px;
        height: 40px;
        border-radius: 50%;
        border: 3px solid rgba(0, 255, 255, 0.65);
        box-shadow: 0 0 18px rgba(0, 255, 255, 0.45);
        pointer-events: none;
        z-index: 1500;
        opacity: 0.95;
        transition: all 0.28s ease-out;
      `;
      document.body.appendChild(ring);

      requestAnimationFrame(() => {
        ring.style.width = '520px';
        ring.style.height = '520px';
        ring.style.opacity = '0';
      });
      setTimeout(() => ring.remove(), 320);

      if (uiManager?.createMissionMsg && name) {
        uiManager.createMissionMsg(String(name).toUpperCase(), 'success');
      }
    },

    // Enemy death explosion
    playEnemyExplosion: (position, enemyType = 'normal') => {
      const colors = {
        normal: 0xff6600,
        fighter: 0xff0000,
        boss: 0xff00ff
      };

      const counts = {
        normal: 45,
        fighter: 32,
        boss: 140
      };

      const explosion = createParticleExplosion(
        position,
        colors[enemyType] || colors.normal,
        counts[enemyType] || counts.normal,
        enemyType === 'boss' ? 5 : 3
      );

      if (explosion) {
        activeEffects.particles.push(explosion);
      }

      // Sprite sheet burst (uses the UI icon pack asset, best effort).
      spawnExplosionSheetSprite(position, { size: enemyType === 'boss' ? 170 : (enemyType === 'fighter' ? 90 : 120) });

      // Chunky debris reads well at N64-ish resolution.
      const debris = createDebrisBurst(
        position,
        colors[enemyType] || colors.normal,
        enemyType === 'boss' ? 18 : (enemyType === 'fighter' ? 10 : 12),
        enemyType === 'boss' ? 12 : 8,
        enemyType === 'boss' ? 1.6 : 1.1
      );
      if (debris) activeEffects.particles.push(debris);

      // Show damage number
      const scoreValues = { normal: 100, fighter: 100, boss: 5000 };
      showDamageNumber(scoreValues[enemyType] || 100, position, enemyType === 'boss');

      // Screen shake for boss
      if (enemyType === 'boss') {
        shakeIntensity = 0.3;
        shakeDuration = 0.5;
      }
    },

    // Projectile hit effect
    playProjectileHit: (position, isPlayer = true) => {
      const color = isPlayer ? 0x00ffff : 0xff0000;
      const explosion = createParticleExplosion(position, color, 14, 1.6);
      if (explosion) {
        activeEffects.particles.push(explosion);
      }
      // Replace the "blue squares" look with a textured shield impact flash.
      spawnShieldImpactSprite(position, { size: isPlayer ? 34 : 26, ttl: 0.28, opacity: 0.92 });
    },

    // Create weapon trail
    createWeaponTrail: (startPos, endPos, color) => {
      createWeaponTrailEffect(startPos, endPos, color);
    },

    // Shield recharge effect
    playShieldRecharge: () => {
      const recharge = document.createElement('div');
      recharge.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: radial-gradient(circle, rgba(0, 255, 255, 0.2), transparent 70%);
        pointer-events: none;
        z-index: 1400;
        opacity: 0;
        transition: opacity 0.3s ease;
      `;
      document.body.appendChild(recharge);

      recharge.style.opacity = '1';
      setTimeout(() => {
        recharge.style.opacity = '0';
        setTimeout(() => recharge.remove(), 300);
      }, 500);

      if (uiManager.createMissionMsg) {
        uiManager.createMissionMsg("SHIELDS RECHARGED", 'success');
      }
    },

    // Power-up collected effect
    playPowerUpEffect: (type, position) => {
      const colors = {
        laser: 0x00ff00,
        shield: 0x00ffff,
        bomb: 0xff6600,
        life: 0xff0000,
        boost: 0xff00ff
      };

      const explosion = createParticleExplosion(
        position,
        colors[type] || 0xffffff,
        25,
        2
      );

      if (explosion) {
        activeEffects.particles.push(explosion);
      }

      // Screen flash
      const colorHex = colors[type] || 0xffffff;
      const colorStr = new THREE.Color(colorHex).getStyle();
      createScreenFlash(colorStr.replace('rgb', 'rgba').replace(')', ', 0.2)'), 0.3, 0.2);
    },

    // Update all effects
    update: (deltaTime) => {
      // Handle screen shake
      if (shakeDuration > 0) {
        shakeDuration -= deltaTime;

        if (sceneManager.camera) {
          const shakeX = (Math.random() - 0.5) * shakeIntensity * 10;
          const shakeY = (Math.random() - 0.5) * shakeIntensity * 10;

          if (originalCameraPos) {
            sceneManager.camera.position.x = originalCameraPos.x + shakeX;
            sceneManager.camera.position.y = originalCameraPos.y + shakeY;
          }
        }

        if (shakeDuration <= 0) {
          // Reset camera
          if (sceneManager.camera && originalCameraPos) {
            sceneManager.camera.position.copy(originalCameraPos);
            shakeIntensity = 0;
          }
        }
      }

      // Update particles
      for (let i = activeEffects.particles.length - 1; i >= 0; i--) {
        const effect = activeEffects.particles[i];

        if (effect.update) {
          const stillActive = effect.update(deltaTime);
          const isExpired = effect.isExpired && effect.isExpired();
          // If an effect doesn't explicitly return `false`, keep it alive until it expires.
          if ((stillActive === false) || isExpired) {
            if (effect.dispose) effect.dispose();
            activeEffects.particles.splice(i, 1);
          }
        } else if (effect.isExpired && effect.isExpired()) {
          if (effect.dispose) effect.dispose();
          activeEffects.particles.splice(i, 1);
        }
      }
    },

    // Cleanup
    dispose: () => {
      activeEffects.particles.forEach(effect => {
        if (effect.dispose) effect.dispose();
      });
      activeEffects.particles = [];
      activeEffects.damageNumbers = [];
      activeEffects.screenOverlays = [];
    }
  };
}
